import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Apple,
  ArrowDownToLine,
  CheckCircle2,
  ChevronRight,
  Cpu,
  History,
  Info,
  Laptop,
  LockKeyhole,
  PhoneCall,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { platforms, versionHistory } from "@/data/releases";

const android = platforms.android;
const recommendedBuild = android.builds.find((build) => build.recommended);

const features = [
  "Simple CyberX account login",
  "Multiple accounts with quick switching",
  "Live call controls, transfer and DTMF",
  "Contacts, call history and one-tap redial",
  "Background registration and start-on-boot",
  "CyberX Server connectivity diagnostics",
];

const installationSteps = [
  "Download the APK that matches your device.",
  "Open the downloaded APK from your browser or file manager.",
  "If Android asks, allow installation from that source temporarily.",
  "Install CyberX Calls, then open the app.",
  "Sign in with your CyberX Calls username and password.",
  "After installation, you can turn off the install-unknown-apps permission again.",
];

const platformCards = [
  { ...platforms.android, Icon: Smartphone, accent: "bg-emerald-100 text-emerald-700" },
  { ...platforms.windows, Icon: Laptop, accent: "bg-blue-100 text-blue-700" },
  { ...platforms.macos, Icon: Apple, accent: "bg-slate-200 text-slate-700" },
];

const useDownloadsMetadata = () => {
  useEffect(() => {
    const title = "Download CyberX Calls Softphone | Android, Windows & macOS";
    const description = "Download the official CyberX Calls softphone for Android. Windows and macOS versions are coming soon.";
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute("content");

    document.title = title;
    descriptionMeta?.setAttribute("content", description);

    return () => {
      document.title = previousTitle;
      if (descriptionMeta && previousDescription) descriptionMeta.setAttribute("content", previousDescription);
    };
  }, []);
};

const DownloadsPage = () => {
  useDownloadsMetadata();

  return (
    <div className="overflow-hidden bg-white" data-testid="downloads-page">
      <section className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-br from-white via-red-50/60 to-white py-16 md:py-24">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-red-200/30 blur-3xl" />
        <div className="container-custom relative">
          <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_0.85fr]">
            <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 shadow-sm">
                <Smartphone className="h-4 w-4" /> Official Android app available
              </div>
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.22em] text-red-600">CyberX Softphones</p>
              <h1 className="max-w-3xl text-4xl font-bold leading-tight text-slate-900 sm:text-5xl md:text-6xl">
                Your CyberX Calls experience, on every device.
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600">
                Download the official CyberX Calls softphone and connect to your SIP account directly from your device. Android is available now, with desktop apps on the way.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={`/downloads/${recommendedBuild.filename}`}
                  download
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-red-600 px-7 py-4 font-semibold text-white shadow-lg shadow-red-600/20 transition hover:-translate-y-0.5 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
                >
                  <ArrowDownToLine className="h-5 w-5" /> Download ARM64 APK
                </a>
                <a href="#all-builds" className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-4 font-semibold text-slate-700 transition hover:border-red-300 hover:text-red-600">
                  View all CPU versions <ChevronRight className="h-5 w-5" />
                </a>
              </div>
              <p className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                <ShieldCheck className="h-4 w-4 text-emerald-600" /> Version {android.version} · {android.minimumAndroid}
              </p>
            </motion.div>

            <motion.div className="relative mx-auto w-full max-w-md" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.65, delay: 0.1 }}>
              <div className="rounded-[2rem] border border-slate-200 bg-slate-950 p-7 text-white shadow-2xl">
                <div className="flex items-center justify-between gap-4">
                  <img src="/logo.png" alt="CyberX Calls" className="h-11 w-auto rounded bg-white px-2 py-1" />
                  <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-300">AVAILABLE</span>
                </div>
                <div className="flex min-h-64 flex-col items-center justify-center py-8">
                  <div className="flex h-24 w-24 items-center justify-center rounded-3xl bg-red-600 shadow-xl shadow-red-600/30">
                    <PhoneCall className="h-12 w-12" />
                  </div>
                  <h2 className="mt-6 text-2xl font-semibold">CyberX Calls</h2>
                  <p className="mt-2 text-center text-sm leading-6 text-slate-400">Modern calling. Simple SIP access. Built for your CyberX account.</p>
                </div>
                <div className="grid grid-cols-2 gap-3 border-t border-slate-800 pt-5 text-center text-xs text-slate-400">
                  <span>Secure account storage</span><span>Background registration</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-100 bg-white py-10" aria-labelledby="platforms-heading">
        <div className="container-custom">
          <h2 id="platforms-heading" className="sr-only">Platform availability</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {platformCards.map(({ name, status, description, Icon, accent }) => (
              <article key={name} className="flex min-w-0 items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className={`flex h-12 w-12 flex-none items-center justify-center rounded-xl ${accent}`}><Icon className="h-6 w-6" /></div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900">CyberX Calls for {name}</h3>
                  <p className={`mt-1 text-sm font-semibold ${status === "available" ? "text-emerald-700" : "text-red-600"}`}>{status === "available" ? "Available now" : "Coming soon"}</p>
                  {description && <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-light" id="all-builds">
        <div className="container-custom">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-red-600">Android {android.version}</p>
            <h2 className="mt-3 text-3xl font-bold text-slate-900 md:text-4xl">Choose your device version</h2>
            <p className="mt-4 text-slate-600">ARM64 is the right choice for nearly all modern Android phones. The other builds support older devices and Intel-based emulators.</p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {android.builds.map((item, index) => (
              <motion.article
                key={item.architecture}
                className={`relative min-w-0 rounded-2xl border p-5 transition hover:-translate-y-1 hover:shadow-xl sm:p-6 ${item.recommended ? "border-red-300 bg-red-50/60 shadow-sm" : "border-slate-200 bg-white"}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.06 }}
                viewport={{ once: true }}
              >
                {item.recommended && <span className="absolute right-4 top-4 rounded-full bg-red-600 px-3 py-1 text-[0.65rem] font-bold text-white sm:right-5 sm:top-5 sm:text-xs">RECOMMENDED</span>}
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white"><Cpu className="h-5 w-5" /></div>
                <h3 className="mt-5 text-xl font-semibold text-slate-900">{item.name}</h3>
                <p className="mt-1 break-all font-mono text-xs text-red-600">{item.architecture}</p>
                <p className="mt-3 text-sm leading-6 text-slate-600 sm:min-h-12">{item.description}</p>
                <p className="mt-3 break-all text-xs text-slate-400">{item.filename}</p>
                <div className="mt-6 flex flex-col gap-4 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm text-slate-500">APK · {item.size}</span>
                  <a href={`/downloads/${item.filename}`} download className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2">
                    Download APK <ArrowDownToLine className="h-4 w-4" />
                  </a>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 h-5 w-5 flex-none text-red-600" />
              <div>
                <h3 className="font-semibold text-slate-900">Which Android version should I download?</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600"><strong>ARM64</strong> is recommended for most recent phones and tablets. Choose <strong>ARM 32-bit</strong> only for an older 32-bit ARM device. <strong>x86_64</strong> and <strong>x86</strong> are mainly for Intel-based Android devices and emulators.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-alt">
        <div className="container-custom grid gap-12 lg:grid-cols-2 lg:items-start">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-red-600">CyberX Calls for Android</p>
            <h2 className="mt-3 text-3xl font-bold text-slate-900 md:text-4xl">Everything essential, without the clutter.</h2>
            <p className="mt-4 leading-7 text-slate-600">The official Android softphone for CyberX Calls accounts, with calling, account management, contacts, DTMF capture and practical connectivity diagnostics in one focused app.</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {features.map((feature) => <div key={feature} className="flex items-start gap-3 text-slate-700"><CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-red-600" /><span>{feature}</span></div>)}
            </div>
          </div>
          <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200 sm:p-7">
            <h3 className="text-xl font-semibold text-slate-900">Release information</h3>
            <dl className="mt-6 grid gap-x-6 gap-y-5 sm:grid-cols-2">
              {[
                ["App", android.appName],
                ["Current version", android.version],
                ["Version code", android.versionCode],
                ["Release date", android.releaseDate],
                ["Android requirement", android.minimumAndroid],
                ["Available builds", `${android.builds.length} CPU architectures`],
              ].map(([label, value]) => <div key={label}><dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</dt><dd className="mt-1 text-sm font-medium text-slate-800">{value}</dd></div>)}
            </dl>
            <div className="mt-7 flex items-center gap-4 border-t border-slate-100 pt-6">
              <div className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-red-100"><LockKeyhole className="h-6 w-6 text-red-600" /></div>
              <div className="min-w-0"><h4 className="font-semibold text-slate-900">Built for CyberX accounts</h4><p className="break-all text-sm text-slate-500">Connects to {android.sipServer}</p></div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-light">
        <div className="container-custom grid gap-10 lg:grid-cols-2">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-red-600">Version {android.version}</p>
            <h2 className="mt-3 text-3xl font-bold text-slate-900 md:text-4xl">What’s new</h2>
            <ul className="mt-7 space-y-4">
              {android.changes.map((change) => <li key={change} className="flex items-start gap-3 text-slate-700"><CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-emerald-600" /><span>{change}</span></li>)}
            </ul>
            {versionHistory.map((release) => (
              <details key={release.version} className="mt-7 rounded-2xl border border-slate-200 bg-white p-5">
                <summary className="flex cursor-pointer list-none items-center gap-3 font-semibold text-slate-900"><History className="h-5 w-5 text-red-600" /> Version {release.version} history</summary>
                <ul className="mt-5 space-y-3 border-t border-slate-100 pt-5 text-sm leading-6 text-slate-600">
                  {release.changes.map((change) => <li key={change} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-red-500" />{change}</li>)}
                </ul>
              </details>
            ))}
          </div>

          <div>
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-red-600">Android installation</p>
            <h2 className="mt-3 text-3xl font-bold text-slate-900 md:text-4xl">How to install</h2>
            <ol className="mt-7 space-y-4">
              {installationSteps.map((step, index) => <li key={step} className="flex items-start gap-4"><span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">{index + 1}</span><span className="pt-1 text-slate-700">{step}</span></li>)}
            </ol>
            <div className="mt-7 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-950">
              Because the APK is distributed directly by CyberX Calls rather than Google Play, Android may show an install-from-this-source message. Download only from this official page.
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-100 bg-white py-14">
        <div className="container-custom text-center">
          <Smartphone className="mx-auto h-8 w-8 text-red-600" />
          <h2 className="mt-4 text-2xl font-bold text-slate-900">Need help choosing a build?</h2>
          <p className="mx-auto mt-3 max-w-xl text-slate-600">Start with ARM64. If Android reports that the app is incompatible, check your device CPU architecture before trying another build.</p>
        </div>
      </section>
    </div>
  );
};

export default DownloadsPage;
